import { TableInputChoices } from "@tellescope/types-models";
import { DatePicker, Input } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { DatabaseSelectField } from "../DatabaseSelectField/DatabaseSelectField";
import { formatDate } from "@tellescope/utils";
import { parseDatabaseRecordResponse } from "../DatabaseSelectField";
import { parseDateStringAnswer, toDateStringAnswer } from "../../utils";

export type InputTableCellProps<T extends keyof TableInputChoices> = TableInputChoices[T] & {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
};

export const InputTableCell = <T extends keyof TableInputChoices>({
  id,
  type,
  autoFocus,
  ...rest
}: InputTableCellProps<T>) => {
  switch (type) {
    case "Database": {
      const { value, onChange } = rest as Omit<InputTableCellProps<"Database">, "type">;
      return (
        <DatabaseSelectField
          value={parseDatabaseRecordResponse(value)}
          id={id}
          onChange={(newVal) => onChange(JSON.stringify(newVal))}
          options={{
            databaseId: "databaseId" in rest.info ? rest.info.databaseId : undefined,
            databaseLabel: "databaseLabel" in rest.info ? rest.info.databaseLabel : undefined,
          }}
          autoFocus={autoFocus}
        />
      );
    }
    case "Text": {
      const { value, onChange, label } = rest as Omit<InputTableCellProps<"Text">, "type">;
      return (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} autoFocus={autoFocus} />
      );
    }
    case "Select": {
      const { value, onChange, label, info } = rest as Omit<InputTableCellProps<"Select">, "type">;
      return (
        <Select value={value} onValueChange={onChange}>
          <Select.Trigger autoFocus={autoFocus}>{value || label}</Select.Trigger>
          <Select.Content>
            {info.choices.map((choice) => (
              <Select.Item key={choice} value={choice}>
                {choice}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      );
    }
    case "Date": {
      const { value, onChange } = rest as Omit<InputTableCellProps<"Date">, "type">;
      const parsed = value ? parseDateStringAnswer(value) : null;
      return (
        <DatePicker
          value={parsed ? new Date(parsed.year, parsed.month - 1, parsed.day) : undefined}
          onChange={(value) => onChange(value ? toDateStringAnswer(value) : "")}
          placeholder={formatDate(new Date(), "MM/DD/YYYY")}
          label=""
          displayFormat="MM/DD/YYYY"
          inputProps={{ autoFocus }}
        />
      );
    }
  }
};
